import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Button from "../Reusable/Button";
import Card from "../Reusable/Card";
import { default as Modal, default as PopupModal } from "../Reusable/Modal";
import CreateItems from "../Sales/CreateItems";
import CreateParty from "../Sales/CreateParty";
import Form from "./Form";
import { ServiceSalesSchema, TradingSalesSchema } from "../../validation/Sales";
import { getKey } from "../../helpers/sessionKey";

const EditSalesReturn = (props: any) => {
  const {
    handleEdit,
    isLoading,
    data,
    openPrintPopup,
    companyData,
    isRevertLoading,
    handleRevert,
    handlePrint,
    handleCancel,
    setOpenPrintPopup,
    isPrintLoading,
  } = props;

  const navigate = useNavigate();
  const [openUserFormPopup, setOpenUserFormPopup] = useState(false);
  const [isOpenItemsPopup, setIsOpenItemsPopup] = useState(false);
  const [openCancelBiLLPopup, setOpenCancelBillPopup] = useState(false);

  const {
    control,
    register,
    setValue,
    setError,
    getValues,
    clearErrors,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<any>({
    resolver: yupResolver(
      companyData?.type === "Trading" ? TradingSalesSchema : ServiceSalesSchema
    ),
    defaultValues: async () => {
      return {
        ...data,
        default_sales_account: "Sales Return",
        bill_number: data?.bill_number,
        items: data?.items.map((item) => ({
          account: { ...item?.account, is_taxable: item?.is_taxable },
          quantity: item?.quantity,
          rate: Number(item?.rate),
          amount: item?.total,
          hs_code: item?.account?.hs_code,
          discount: item?.discount,
          unit: item?.account?.unit?.name,
        })),
      };
    },
  });
  const items = watch("items");
  const vat_tax = watch("tax_rate");

  useEffect(() => {
    if (!Array.isArray(items)) return;

    let taxableTotal = 0;
    let nonTaxableTotal = 0;
    let itemsValue = 0;
    let totalDiscount = 0;

    items.forEach((item, index) => {
      const amount = Number(item?.amount) || 0;
      const discount = Number(item?.discount) || 0;
      const isTaxable = Boolean(item?.is_taxable);
      itemsValue += amount;
      const amountAfterDiscount = Math.max(amount - discount, 0);
      totalDiscount += discount;
      if (isTaxable) {
        taxableTotal += amountAfterDiscount;
      } else {
        nonTaxableTotal += amountAfterDiscount;
      }
    });

    const netAmount = taxableTotal + nonTaxableTotal;
    const vatTax = taxableTotal * (Number(vat_tax) / 100);
    const totalAmount = netAmount + vatTax;

    setValue("items_value", itemsValue.toFixed(2));
    setValue("vat_tax_amount", vatTax.toFixed(2));
    setValue("total_amount", totalAmount.toFixed(2));
    setValue("taxable_amount", taxableTotal.toFixed(2));
    setValue("non_taxable_amount", nonTaxableTotal.toFixed(2));
    setValue("total_discount", totalDiscount.toFixed(2));
  }, [JSON.stringify(items), setValue, vat_tax]);

  const handleClosePrint = () => {
    setOpenPrintPopup(false);
    navigate("/accounting/sales-return");
  };
  const isSuperUser = getKey("is_superuser");
  const adminOnly = data?.is_draft;

  return (
    <>
      <Card>
        <form
          onSubmit={handleSubmit((data) => handleEdit(data, false, setError))}
        >
          <Form
            control={control}
            register={register}
            errors={errors}
            getValues={getValues}
            setValue={setValue}
            companyData={companyData}
            isEdit={true}
            isDraft={data?.is_draft}
            setOpenUserFormPopup={setOpenUserFormPopup}
            setIsOpenItemsPopup={setIsOpenItemsPopup}
            clearErrors={clearErrors}
            watch={watch}
          />
          <div className={tw`w-full flex gap-x-4 justify-end px-4`}>
            {/* <div className={tw`w-24 `}>
              <Button
                type="button"
                // disabled={!adminOnly}
                label="Cancel"
                isLoading={isLoading}
                onClick={() => setOpenCancelBillPopup(true)}
              />
            </div> */}
            {data?.is_draft ? (
              <div className={tw`w-24 `}>
                <Button
                  type="button"
                  disabled={!adminOnly}
                  label="Save to Draft"
                  isLoading={isLoading}
                  onClick={handleSubmit((data) =>
                    handleEdit(data, true, setError)
                  )}
                />
              </div>
            ) : (
              <div className={tw`w-24 `}>
                <Button
                  type="button"
                  // disabled={!adminOnly}
                  label="Revert"
                  isLoading={isRevertLoading}
                  onClick={() => handleRevert(setError)}
                />
              </div>
            )}
            <div className={tw`w-24`}>
              <Button
                label="Submit"
                disabled={!data?.is_draft}
                isLoading={isLoading}
              />
            </div>
          </div>
        </form>
      </Card>
      <Modal
        title={"Create Party"}
        isOpen={openUserFormPopup}
        setIsOpen={setOpenUserFormPopup}
      >
        <CreateParty
          setOpenUserFormPopup={setOpenUserFormPopup}
          setParty={setValue}
        />
      </Modal>
      <PopupModal
        isOpen={openCancelBiLLPopup}
        setIsOpen={setOpenCancelBillPopup}
        title="Cancel Bill"
      >
        <div className={tw`p-4 mt-10`}>
          <p className={tw`text-gray-600 mb-6`}>
            Are you sure you want to cancel this bill? This action cannot be
            undone.
          </p>

          <div className={tw`flex justify-center gap-3`}>
            <button
              onClick={() => setOpenCancelBillPopup(false)}
              className={tw`px-6 py-2 rounded-md focus:outline-none bg-gray-200 text-gray-800 hover:bg-gray-300 transition`}
            >
              No
            </button>

            <button
              onClick={() => {
                handleCancel();
                setOpenCancelBillPopup(false);
              }}
              className={tw`px-6 py-2 rounded-md  focus:outline-none bg-red-600 text-white hover:bg-red-700 transition`}
            >
              Yes
            </button>
          </div>
        </div>
      </PopupModal>
      <PopupModal
        isOpen={openPrintPopup}
        setIsOpen={handleClosePrint}
        title={"Print"}
      >
        <form action="#" className={tw`w-[600px]`}>
          <Button
            label={"Print "}
            type="button"
            isLoading={isPrintLoading}
            disabled={isPrintLoading}
            onClick={handlePrint}
            autoFocus
          />
          <Button
            type="button"
            label={"Close"}
            onClick={() => handleClosePrint()}
          />
        </form>
      </PopupModal>
      <PopupModal
        isOpen={isOpenItemsPopup}
        setIsOpen={setIsOpenItemsPopup}
        title="Create Items"
      >
        <CreateItems setIsOpenItemsPopup={setIsOpenItemsPopup} />
      </PopupModal>
    </>
  );
};
export default EditSalesReturn;
